import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { Observable, of } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { Period } from '../date-range/date-range.service';
import { DateRangePreset } from '../../models/graphs';
import { DatesService } from '../../../shared/services/dates/dates.service';
import { DateTime } from 'luxon';
import { TLangCode } from '../../../ngrx/locale/states/locale.state';


export interface GeneralStatisticsResult {
  jobs_total: number,
  applications_total: number,
  applications_last_week: number
}

export interface ApplicationStatisticItem {
  day: string;
  count: number;
}

export interface ApplicationStatisticChartData {
  name: string;
  value: number;
  dateFrom?: string;
  dateTo?: string;
}

export interface DevicesStatisticItem {
  name: string;
  value: number;
}

@Injectable({
  providedIn: 'root'
})
export class StatisticsService {
  private statisticUrl = environment.statisticUrl;
  private apiUrl = environment.apiUrl;
  private companyUrl = this.apiUrl + 'companies/';

  datesService = inject(DatesService);

  private readonly MAX_HISTORY_YEARS = 4;

  constructor(
    private readonly http: HttpClient
  ) {
  }

  private _makeUrl(path: string): string {
    return `${this.statisticUrl}${path}`;
  }

  generalCompanyStatistics(
    companyId: number,
    date_from = '',
    date_to = '',
  ): Observable<any> {
    const url = this._makeUrl(`company/?company_id=${companyId}`);

    let params = new HttpParams();

    if (date_from && date_to) {
      params = params.append('date_from', date_from);
      params = params.append('date_to', date_to);
    }

    return this.http.get(url, { params });
  }

  jobApplicationsGraph(
    companyId: number,
    filters: {
      include_jobs?: string,
      exclude_jobs?: string,
      date_from?: string;
      date_to?: string;
      showDeleted?: boolean;
    } = {}
  ): Observable<any> {
    const url = this._makeUrl('jobs/applications-graph');
    const { include_jobs = '', exclude_jobs = '', date_from = '', date_to = '', showDeleted = false } = filters;

    let params = new HttpParams();
    params = params.append('company_id', String(companyId));

    if (include_jobs?.length) {
      params = params.append('include_jobs', include_jobs);
    }

    if (exclude_jobs?.length) {
      params = params.append('exclude_jobs', exclude_jobs);
    }

    if (date_from && date_to) {
      params = params.append('date_from', date_from);
      params = params.append('date_to', date_to);
    }

    params = params.append('show_deleted', showDeleted ? '1' : '0');

    return this.http.get(url, { params });
  }

  assessmentFoldersGraph(
    companyId: number,
    filters: {
      folders?: string[],
      include_jobs?: string,
      exclude_jobs?: string,
      date_from?: string,
      date_to?: string,
      showDeleted?: boolean
    } = {}
  ): Observable<any> {
    const url = this._makeUrl(`company/${companyId}/assessment-folders-graph`);

    const { folders = [], include_jobs = '', exclude_jobs = '', date_from = '', date_to = '', showDeleted = false } = filters;

    let params = new HttpParams();

    if (folders?.length) {
      params = params.append('folders_filter', folders.join(','));
    }

    if (include_jobs?.length) {
      params = params.append('include_jobs', include_jobs);
    }

    if (exclude_jobs?.length) {
      params = params.append('exclude_jobs', exclude_jobs);
    }

    if (date_from && date_to) {
      params = params.append('date_from', date_from);
      params = params.append('date_to', date_to);
    }

    params = params.append('show_deleted', showDeleted ? '1' : '0');

    return this.http.get(url, { params });
  }

  getTopSourcesGraph(
    companyId: number,
    filters: {
      include_jobs?: string,
      exclude_jobs?: string,
      pages?: string[],
      dateFrom?: string,
      dateTo?: string
    } = {}
  ): Observable<any> {
    const url = this._makeUrl(`graphs/${companyId}/jobs-top-sources/`);
    const { include_jobs = '', exclude_jobs = '', pages = [], dateFrom = '', dateTo = '' } = filters;

    let params = new HttpParams();

    if (include_jobs?.length) {
      params = params.append('include_jobs', include_jobs);
    }

    if (exclude_jobs?.length) {
      params = params.append('exclude_jobs', exclude_jobs);
    }

    if (pages.length) {
      pages.forEach(page => {
        params = params.append('pages', page);
      });
    }

    if (dateFrom && dateTo) {
      params = params.append('date_from', dateFrom);
      params = params.append('date_to', dateTo);
    }

    return this.http.get(url, { params });
  }

  getDevicesGraph(
    companyId: number,
    filters: {
      dateFrom?: string,
      dateTo?: string,
      include_jobs?: string,
      exclude_jobs?: string
    } = {}
  ): Observable<DevicesStatisticItem[]> {
    const url = this._makeUrl(`graphs/${companyId}/jobs-devices/`);
    const { dateFrom = '', dateTo = '', include_jobs = '', exclude_jobs = '' } = filters;
    let params = new HttpParams();

    if (dateFrom && dateTo) {
      params = params.append('date_from', dateFrom);
      params = params.append('date_to', dateTo);
    }

    if (include_jobs?.length) {
      params = params.append('include_jobs', include_jobs);
    }

    if (exclude_jobs?.length) {
      params = params.append('exclude_jobs', exclude_jobs);
    }

    return this.http.get<DevicesStatisticItem[]>(url, { params });
  }

  jobsFilter(
    companyId: number,
    filters: {
      q?: string,
      page?: number,
      per_page?: number
    }
  ): Observable<any> {
    const url = this._makeUrl('jobs/filter');

    let params = new HttpParams({ fromObject: filters });
    params = params.append('company_id', String(companyId));
    params = params.append('per_page', 1000); // todo remove when frontend correctly handles pagination

    return this.http.get<any[]>(url, { params });
  }

  public getSubscribers(companyId: number): Observable<any> {
    return this.http.get(`${this.companyUrl}${companyId}/followers/amount/`);
  }

  getChartLegendColor(colors: string, index: number): string {
    return colors[index % colors.length];
  }

  addNumbersToDuplicates(data): any[] {
    // Sort data by title
    data.sort((a, b) => a.name.localeCompare(b.name));

    // Reduce method to handle duplicates
    let counter = 2;

    const processedData = data.reduce((acc, el, index) => {
      const baseTitle = el.name;
      if (index > 0 && data[index].name === data[index - 1].name) {
        el.name = `${baseTitle} (${counter})`;
        counter++;
      } else {
        counter = 2;
      }
      acc.push(el);
      return acc;
    }, []);

    // Sort processed data by applications in descending order
    processedData.sort((a, b) => b.value - a.value);

    return processedData;
  }

  getDonationAmount(): Observable<{ count: number }> {
    const url = this._makeUrl('applications/save-the-children');

    return this.http.get<{ count: number }>(url);
  }

  getGeneralCompanyStatistics(companyId: number): Observable<GeneralStatisticsResult> {
    const url = this._makeUrl(`company/${companyId}/dashboard/general`);

    return this.http.get<GeneralStatisticsResult>(url);
  }

  public getApplicationStatistics(
    companyId: number,
    start: string,
    end: string,
    showDeleted?: boolean
  ): Observable<ApplicationStatisticItem[]> {
    const url = this._makeUrl(`company/${companyId}/dashboard/applications`);
    const isShowDeleted = showDeleted ? '1' : '0';
    const params = new HttpParams({
      fromObject: {
        date_from: start,
        date_to: end,
        show_deleted: isShowDeleted
      }
    });

    return this.http.get<ApplicationStatisticItem[]>(url, { params });
  }

  aggregateDataByDateRange(
    { range, serverData, minStartDate }: { range: Period[]; serverData: ApplicationStatisticItem[]; minStartDate?: Date | string }
  ): ApplicationStatisticChartData[] {
    const filteredRange = minStartDate
      ? range.filter(item => new Date(item.dateTo) >= new Date(minStartDate))
      : range;

    return filteredRange.map(item => {
      const datesInRange = [];

      serverData.forEach(el => {
        const compareDate = el.day;
        const startDate = item.dateFrom;
        const endDate = item.dateTo;

        if (compareDate > startDate && compareDate < endDate ||
          compareDate === endDate) {
          datesInRange.push(el);
        }
      });

      const value = datesInRange.reduce((acc, el) => acc += el.count, 0);

      return {
        ...item,
        value: value || 0,
      };
    });
  }

  /**
   * Statistics start date: the later of company creation date or 5 years ago (max 5 years of history)
   * Returns January 1st of the year that is MAX_HISTORY_YEARS ago if company creation date is older
   * @param companyCreateDate - company creation date
   */
  getStartTrackingDate(companyCreateDate: Date): Date {
    const now = new Date();
    const yearLimit = now.getFullYear() - this.MAX_HISTORY_YEARS;
    const pastLimit = new Date(yearLimit, 0, 1); // January 1st of the limit year

    return new Date(companyCreateDate) > pastLimit ? companyCreateDate : pastLimit;
  }

  getJobFilterIds(checkedJobs: number[], allJobs: number[]): { include_jobs?: string } | { exclude_jobs?: string } {
    if (checkedJobs?.length === allJobs.length) {
      return {};
    }

    const halfLength = Math.ceil(allJobs.length / 2);

    if (checkedJobs.length < halfLength) {
      return {
        include_jobs: checkedJobs?.join(',')
      };
    }

    const excludedJobs = allJobs.filter(jobId => !checkedJobs.includes(jobId))?.join(',');

    return {
      exclude_jobs: excludedJobs
    };
  }

  getDateRange(type: DateRangePreset, minDate: Date): { start: string; end: string } {
    const now = this.datesService.now();
    let start: string;
    let end: string = now.toISODate();

    switch (type) {
      case DateRangePreset.LAST_7_DAYS: {
        start = now.minus({ days: 6 }).toISODate();
        break;
      }

      case DateRangePreset.LAST_30_DAYS: {
        start = now.minus({ days: 29 }).toISODate();
        break;
      }

      case DateRangePreset.LAST_90_DAYS: {
        start = now.minus({ days: 89 }).toISODate();
        break;
      }

      case DateRangePreset.THIS_MONTH: {
        start = now.startOf('month').toISODate();
        end = now.toISODate();
        break;
      }

      case DateRangePreset.LAST_MONTH: {
        const lastMonth = now.minus({ months: 1 });
        start = lastMonth.startOf('month').toISODate();
        end = lastMonth.endOf('month').toISODate();
        break;
      }

      case DateRangePreset.YEAR_TO_DATE:
        start = now.startOf('year').toISODate();
        end = now.toISODate();
        break;

      case DateRangePreset.LAST_YEAR: {
        const lastYear = now.minus({ years: 1 });
        start = lastYear.startOf('year').toISODate();
        end = lastYear.endOf('year').toISODate();
        break;
      }

      case DateRangePreset.ALL_TIME: {
        if (minDate && !isNaN(minDate.getTime())) {
          // Convert Date object to 'yyyy-MM-dd' format using DatesService
          start = this.datesService.fromJSDate(minDate).toISODate();
        } else {
          start = now.minus({ years: 5 }).toISODate();
        }
        end = now.toISODate();
        break;
      }

      default:
        start = now.minus({ days: 7 }).toISODate();
    }

    return { start, end };
  }

  /**
   * Aggregates data by appropriate time periods based on date range:
   * - Up to 7 days: daily aggregation
   * - 8 days to 2 months: weekly aggregation
   * - 2 months to 1 year: monthly aggregation
   * - Over 1 year: yearly aggregation
   *
   * @param startDate - Start date in 'YYYY-MM-DD' format
   * @param endDate - End date in 'YYYY-MM-DD' format
   * @param data - Array of data items with day and count properties
   * @param locale - Locale for date formatting (default is 'en')
   * @returns Array formatted for ngx-charts with name and value properties
   */
  aggregateDataForChart(
    startDate: string,
    endDate: string,
    data: ApplicationStatisticItem[],
    locale: TLangCode = 'en'
  ): ApplicationStatisticChartData[] {
    const start = this.datesService.fromISO(startDate);
    const end = this.datesService.fromISO(endDate);
    const daysDiff = end.diff(start, 'days').days;

    // Create a map for quick lookup of data by day
    const dataMap = new Map<string, number>();
    data.forEach(item => {
      dataMap.set(item.day, item.count);
    });

    if (daysDiff < 7) {
      // Daily aggregation
      return this.aggregateByDay(start, end, dataMap, locale);
    } else if (daysDiff <= 60) {
      // Weekly aggregation (8 days to 2 months)
      return this.aggregateByWeek(start, end, dataMap, locale);
    } else if (daysDiff <= 365) {
      // Monthly aggregation (2 months to 1 year)
      return this.aggregateByMonth(start, end, dataMap, locale);
    } else {
      // Yearly aggregation (over 1 year)
      return this.aggregateByYear(start, end, dataMap);
    }
  }

  private aggregateByDay(
    start: DateTime,
    end: DateTime,
    dataMap: Map<string, number>,
    locale: TLangCode = 'en'
  ): ApplicationStatisticChartData[] {
    const result: ApplicationStatisticChartData[] = [];
    let current = start;

    while (current <= end) {
      const dateKey = current.toISODate();
      const value = dataMap.get(dateKey) || 0;

      result.push({
        name: current.setLocale(locale).toFormat('MMM d'),
        value,
        dateFrom: dateKey,
        dateTo: dateKey
      });

      current = current.plus({ days: 1 });
    }

    return result;
  }

  private aggregateByWeek(
    start: DateTime,
    end: DateTime,
    dataMap: Map<string, number>,
    locale: TLangCode = 'en'
  ): ApplicationStatisticChartData[] {
    const result: ApplicationStatisticChartData[] = [];
    let weekStart = start;

    while (weekStart <= end) {
      // Find the next Sunday (weekday = 7)
      const dayOfWeek = weekStart.weekday; // Monday = 1, Sunday = 7
      const daysUntilSunday = dayOfWeek === 7 ? 0 : 7 - dayOfWeek;
      const weekEnd = weekStart.plus({ days: daysUntilSunday });

      const actualWeekEnd = weekEnd > end ? end : weekEnd;

      let weekTotal = 0;
      let current = weekStart;

      while (current <= actualWeekEnd) {
        const dateKey = current.toISODate();
        weekTotal += dataMap.get(dateKey) || 0;
        current = current.plus({ days: 1 });
      }

      const startLabel = weekStart.setLocale(locale).toFormat('MMM d');
      const endLabel = actualWeekEnd.setLocale(locale).toFormat('MMM d');

      result.push({
        name: `${startLabel} - ${endLabel}`,
        value: weekTotal,
        dateFrom: weekStart.toISODate(),
        dateTo: actualWeekEnd.toISODate()
      });

      // Next week starts on Monday after the Sunday
      weekStart = actualWeekEnd.plus({ days: 1 });
    }

    return result;
  }

  private aggregateByMonth(
    start: DateTime,
    end: DateTime,
    dataMap: Map<string, number>,
    locale: string = 'en'
  ): ApplicationStatisticChartData[] {
    const result: ApplicationStatisticChartData[] = [];
    let current = start.startOf('month');


    while (current <= end) {
      const monthStart = current > start ? current : start;
      const monthEnd = current.endOf('month') < end ? current.endOf('month') : end;

      let monthTotal = 0;
      let day = monthStart;

      while (day <= monthEnd) {
        const dateKey = day.toISODate();
        monthTotal += dataMap.get(dateKey) || 0;
        day = day.plus({ days: 1 });
      }

      const monthYear = monthEnd.setLocale(locale).toFormat('MMM');

      result.push({
        name: `${monthYear}`,
        value: monthTotal,
        dateFrom: monthStart.toISODate(),
        dateTo: monthEnd.toISODate()
      });

      current = current.plus({ months: 1 }).startOf('month');
    }

    return result;
  }

  private aggregateByYear(start: DateTime, end: DateTime, dataMap: Map<string, number>): ApplicationStatisticChartData[] {
    const result: ApplicationStatisticChartData[] = [];
    let current = start.startOf('year');

    while (current <= end) {
      const yearStart = current > start ? current : start;
      const yearEnd = current.endOf('year') < end ? current.endOf('year') : end;

      let yearTotal = 0;
      let day = yearStart;

      while (day <= yearEnd) {
        const dateKey = day.toISODate();
        yearTotal += dataMap.get(dateKey) || 0;
        day = day.plus({ days: 1 });
      }

      result.push({
        name: current.toFormat('yyyy'),
        value: yearTotal,
        dateFrom: yearStart.toISODate(),
        dateTo: yearEnd.toISODate()
      });

      current = current.plus({ years: 1 }).startOf('year');
    }

    return result;
  }
}
